// ══════════════════════════════════════════════════════════════════════════════════════════════
//  «¿ENTRAR Y DESBLOQUEAR CON LA HUELLA O LA CARA EN ESTE DISPOSITIVO?» · LA OFERTA, UNA VEZ  (5-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  El dueño: «También debería ofrecerme registrarlo si aún no lo tengo activo en mi móvil, así aumentamos la seguridad».
//  Sale UNA vez por arranque, ya dentro y con la pantalla sin bloquear, a quien en ESTE dispositivo puede activarlo y no
//  lo tiene (services/huellaOCara.esteDispositivo). «Ahora no» no vuelve a preguntar en este dispositivo en
//  DIAS_SIN_PREGUNTAR días; se puede activar siempre en Mi perfil, Seguridad. En la app pide la contraseña (la llave del
//  móvil se da con ella; la casilla lleva el usuario de la sesión para que el llavero la ofrezca); en el navegador, solo
//  la huella o la cara.
//  NO sale: a la cuenta del quiosco de fichar ni a la de limpieza, en las tablets, a quien tiene que cambiar la
//  contraseña, ni en una app vieja o un aparato sin lector (ahí no hay nada que ofrecer: lo explica Mi perfil).
//  NO toca la sesión: no pide nada al servidor que pueda echar a nadie; si algo falla, se dice y se cierra.
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { usuarioDeLaSesion, contrasenaDelFormulario } from '../utils/reautenticar'
import { CLAVE_BLOQUEO_ACTIVO, pantallaBloqueada } from '../services/sesion'
import {
  esteDispositivo, activarAqui, ofertaPospuesta, posponerOferta, yaOfrecida, apuntarOfrecida, explicarFalloHuella, contarFallo,
  DIAS_SIN_PREGUNTAR,
} from '../services/huellaOCara'
import { t as i18nT, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

const ESPERA_MS = 4000

// `espera`: lo que tarda en salir tras entrar (de fábrica ESPERA_MS; las guardias la ponen a 0).
export default function OfertaHuellaOCara({ espera = ESPERA_MS } = {}) {
  const { user, token } = useAuth()
  const [aqui, setAqui] = useState(null)
  const [ver, setVer] = useState(false)
  const [pass, setPass] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState('')
  const formRef = useRef(null)

  useEffect(() => {
    if (!token || !user || !user.id) return undefined
    const rol = String(user.rol || '')
    if (rol === 'control_horario' || rol === 'limpieza' || user.must_change_password) return undefined
    let vivo = true
    const t = setTimeout(async () => {
      try {
        if (typeof window !== 'undefined' && /^\/(tablet|portal|login)/.test(window.location.pathname || '')) return
        if (yaOfrecida()) return
        // Con la pantalla de bloqueo puesta (la marca guarda DE QUIÉN es el candado), no se ofrece nada encima.
        let bloqueada = false
        try { bloqueada = pantallaBloqueada() || !!localStorage.getItem(CLAVE_BLOQUEO_ACTIVO) } catch { bloqueada = false }
        if (bloqueada) return
        const a = await esteDispositivo(user.id)
        if (!vivo || !a || !a.puede || a.activado) return
        if (ofertaPospuesta(a.tipo, user.id)) return
        apuntarOfrecida()
        setAqui(a); setVer(true)
      } catch { /* sin oferta: no pasa nada */ }
    }, espera)
    return () => { vivo = false; clearTimeout(t) }
  }, [token, user?.id])

  if (!ver || !aqui) return null

  const ahoraNo = () => { posponerOferta(user.id, true, Date.now(), aqui.tipo); setVer(false) }
  const activar = async (e) => {
    if (e && e.preventDefault) e.preventDefault()
    setError('')
    const password = aqui.tipo === 'app' ? contrasenaDelFormulario(formRef.current, pass) : null
    if (aqui.tipo === 'app' && !password) { setError(i18nT('components.OfertaHuellaOCara.escribe_tu_contrasena_para_activarlo', 'Escribe tu contraseña para activarlo.')); return }
    setOcupado(true)
    try {
      await activarAqui({ tipo: aqui.tipo, email: user.email, password, agenteId: user.id })
      setPass(''); setVer(false)
    } catch (err) {
      contarFallo(aqui.tipo, err)
      setError(explicarFalloHuella(err))
    } finally { setOcupado(false) }
  }

  return (
    <div className="modal-safe" data-oferta-huella-o-cara={aqui.tipo} style={{ position: 'fixed', inset: 0, zIndex: 99990, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--surface, #fff)', borderRadius: 18, boxShadow: '0 18px 50px rgba(0,0,0,.18)', padding: 24, width: '100%', maxWidth: 400 }}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>{tx('components.OfertaHuellaOCara.entrar_y_desbloquear_con', '¿Entrar y desbloquear con {{nombre}}?', { nombre: aqui.nombre })}</div>
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 14px' }}>
          {tx('components.OfertaHuellaOCara.en_este_dispositivo_entraras_y', 'En este dispositivo entrarás y desbloquearás Biango con {{nombre}} en vez de escribir la contraseña. Es más seguro y más rápido. Tu huella y tu cara no salen del dispositivo. Actívalo solo si este dispositivo es tuyo (en un ordenador compartido, mejor tu PIN o tu contraseña).', { nombre: aqui.nombre })}
        </p>
        <form ref={formRef} onSubmit={activar} style={{ margin: 0 }}>
          {aqui.tipo === 'app' && (
            <>
              <input {...usuarioDeLaSesion(user.email)} />
              <input type="password" name="password" autoComplete="current-password" value={pass} onChange={(x) => setPass(x.target.value)}
                placeholder={i18nT('components.OfertaHuellaOCara.tu_contrasena_una_sola_vez', 'Tu contraseña (una sola vez)')} autoCapitalize="none" autoCorrect="off" spellCheck={false}
                style={{ width: '100%', boxSizing: 'border-box', padding: '11px 13px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 15, background: 'var(--surface)', color: 'var(--text-primary)', marginBottom: 10 }} />
            </>
          )}
          {error && <div role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 10 }}>{error}</div>}
          <button type="submit" disabled={ocupado}
            style={{ width: '100%', padding: '12px 16px', border: 'none', borderRadius: 11, background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: 14.5, fontWeight: 800, cursor: 'pointer' }}>
            {ocupado ? i18nT('components.OfertaHuellaOCara.activando', 'Activando…') : i18nT('components.OfertaHuellaOCara.activar_en_este_dispositivo', 'Activar en este dispositivo')}
          </button>
        </form>
        <button type="button" onClick={ahoraNo} disabled={ocupado}
          style={{ width: '100%', marginTop: 8, padding: '11px 16px', border: '1px solid var(--border)', borderRadius: 11, background: 'var(--surface-2)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          {i18nT('components.OfertaHuellaOCara.ahora_no', 'Ahora no')}
        </button>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8, textAlign: 'center' }}>
          {tx('components.OfertaHuellaOCara.ahora_no_no_te_lo', '«Ahora no» no te lo vuelve a preguntar aquí en {{DIAS_SIN_PREGUNTAR}} días. Lo tienes en Mi perfil, Seguridad.', { DIAS_SIN_PREGUNTAR })}
        </div>
      </div>
    </div>
  )
}
